// @flow
import React from "react";
import PropTypes from 'prop-types';
import {injectIntl, defineMessages} from "react-intl";
import {reisy, use} from "reisy";
import Cover from "../components/cover.js";
import * as Icons from "../components/icons.js";
import DropdownItem from "./dropdown.js";
import {Button} from "./items.js";
import {Close} from "../components/icons.js";
import {isMobile} from "../environment.js";
import {post} from "../xhr.js";
import {trackShare} from "../analytics/analytics.js"
import Heading from '../components/heading';

import type Player from "../player/player.js";
import type {Publishable} from "../api/types.js";

const messages = defineMessages({
	back: {
		id: "share.back",
		defaultMessage: "Back",
	},
	embed: {
		id: "share.embed",
		defaultMessage: "Embed",
	},
	share: {
		id: "share.share",
		defaultMessage: "Share",
	},
	cover: {
		id: "share.cover",
		defaultMessage: "cover",
	},
	custom: {
		id: "share.custom",
		defaultMessage: "custom element",
	},
	inline: {
		id: "share.inline",
		defaultMessage: "direct embedding",
	},
	customhere: {
		id: "share.customhere",
		defaultMessage: "INSERT YOUR CUSTOM ELEMENT HERE",
	},
	scriptonce: {
		id: "share.scriptonce",
		defaultMessage: "You only need to include the above script once per page",
	},
	FacebookLabel: {
		id: "share.FacebookLabel",
		defaultMessage: "Share on Facebook",
	},
	TwitterLabel: {
		id: "share.TwitterLabel",
		defaultMessage: "Tweet",
	},
	TwitterText: {
		id: "share.TwitterText",
		defaultMessage: "{title}",
	},
	LinkedInLabel: {
		id: "share.LinkedInLabel",
		defaultMessage: "Share on LinkedIn",
	},
	LinkedInText: {
		id: "share.LinkedInText",
		defaultMessage: "{title}",
	},
	LinkedInTitle: {
		id: "share.LinkedInTitle",
		defaultMessage: "{title}",
	},
	WhatsAppLabel: {
		id: "share.WhatsAppLabel",
		defaultMessage: "Share on WhatsApp",
	},
	WhatsAppText: {
		id: "share.WhatsAppText",
		defaultMessage: "{title}",
	},
	EMailLabel: {
		id: "share.EMailLabel",
		defaultMessage: "Share via eMail",
	},
	EMailText: {
		id: "share.EMailText",
		defaultMessage: "{share_url}",
	},
	EMailTitle: {
		id: "share.EMailTitle",
		defaultMessage: "{title}",
	},
	EmbedLabel: {
		id: "share.EmbedLabel",
		defaultMessage: "Embed",
	},
	WeiboLabel: {
		id: "share.WeiboLabel",
		defaultMessage: "Weibo",
	},
	WeiboTitle: {
		id: "share.WeiboText",
		defaultMessage: "{title}",
	},
	XingLabel: {
		id: "share.XingLabel",
		defaultMessage: "Share on Xing",
	},
	XingText: {
		id: "share.XingText",
		defaultMessage: "{title}",
	},
});

export const styles = reisy`
@namespace Share;

Collapsed: 112px;
Expanded_Full: 465px;
Expanded_Half: 235px;
options: Facebook Twitter LinkedIn mobile-WhatsApp EMail;
shortenShareURL: true;

/* TODO: redesign the icon to fit in the 24px grid */
ShareButton {
	width: 40px;
	height: 40px;
	padding: 0;
	path {
		stroke: none !important;
	}
}

Container {
	min-width: 250px;
	max-width: 90vw;
	min-height: $Collapsed;
	position: relative;
	overflow: hidden;
	transition: height $Consts.Transition;
}

Float {
	position: fixed;
	top: 5rem;
	min-width: auto;
	min-height: auto;
	pointer-events: all;
}

FloatLeft {
	@extends Float;
	left: 1rem;
}

FloatRight {
	@extends Float;
	right: 1rem;
}

EmbedOpen {
	height: $Expanded_Full;
	$Media.landscapeSmall {
		height: $Expanded_Half;
	}
}
Section {
	background: white;
	min-width: 100%;
	transition: opacity $Consts.Transition, visibility $Consts.Transition 0ms;
	position: relative;
	z-Index: 2;

	$Float &  {
		background: transparent;
	}
}
SectionAbsolute {
	@extends Section;
	position: absolute;
	top: 0;
	z-index: 1;
}
SectionEmbedOpen {
	opacity: 0;
	visibility: hidden;
	/* XXX: wtf? */
	pointer-events: none;
}
Close {
	position: absolute;
	top: 4px;
	right: 0;
	z-index: 10;
	background: transparent;
	border: none;
	cursor: pointer;
	font-size: 18px;
	padding: 8px 12px 8px;
	line-height: 1;
	&:active,
	&:focus,
	&:hover {
		outline: 0;
	}
}
CloseIcon {
	width: 16px;
	height: 16px;
	display: inline-block;
	vertical-align: middle;
}
Row {
	text-align: center;
	font-size: 14px;
	display: block;
	padding: 8px 0 7px;
	font-family: $Fonts.aside;
	font-weight: normal;
	text-transform: uppercase;
	margin: 0 12px 8px;
	border-bottom: 1px solid #999;

	$Float & {
		display: none;
	}
}
Options {
	list-style: none;
	margin: 0;
	padding: 8px 12px;
	box-sizing: border-box;
	display: flex;
	min-width: 100%;
	justify-content: space-around;

	@media (max-width: 600px) {
		flex-wrap: wrap;
	}

	$Float & {
		flex-direction: column;
		padding: 0;
	}
}
Item {
	flex: 0 0 auto;

	$Float & svg * {
		fill: transparent;
		stroke: #fff;
		stroke-width: 1;
		vector-effect: non-scaling-stroke;
	}
}
Option {
	display: block;
	cursor: pointer;
	height: 48px;
	width: 48px;
}
EmbedCode {
	box-sizing: border-box;
	display: block;
	height: 88px;
	width: 90%;
	margin: 8px auto 0;
	resize: none;
	line-height: 1.2em;
	font-size: 13px;
	font-family: monospace;
	overflow-x: hidden;
	word-wrap: break-word;
	border: 1px solid #000;
	$Media.landscapeSmall {
		margin-top: 0;
	}
}
EmbedCodeBig {
	height: 321px;
	margin-top: 0;
	$Media.landscapeSmall {
		height: 88px;
	}
}
EmbedCover {
	font-size: 9px;
	margin: auto;
	overflow: hidden;
	$Media.landscapeSmall {
		display: none;
	}
}
EmbedSizeContainer {
	position: relative;
	width: 90%;
	margin: 0 auto 8px;
	padding: 0;
	border-bottom: none;
}
EmbedSize {
	width: 100%;
	appearance: none;
	font-size: inherit;
	font-family: inherit;
	position: relative;
	border-radius: 0;
	padding: 0.5em 30px 0.5em 0.5em;
	border: 1px solid #000;
	line-height: 1.4em;
	outline: 0;
	background: #fff;

	&::-ms-expand {
		display: none;
	}
}
EmbedSizeIcon {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	right: 8px;
	height: 16px;
	width: 16px;
	pointer-events: none;
}
EmbedBack {
	cursor: pointer;
	padding: 8px;
	margin: 0;
	border-bottom: none;
	&:hover {
		text-decoration: underline;
	}
}
ShareIcon {
	white-space: nowrap;
	overflow: hidden;
}

ShareIconLink {
	@extends Navigation.Item;
	display: block;
	width: $Navigation.itemHeight;
	height: $Navigation.itemHeight;
	padding: 0;
	cursor: pointer;

	& > div {
		height: 100%;
	}

	span {
		visibility: hidden;
	}
}
`;

const SERVICES = {
	Facebook: {
		url(url) { return `https://facebook.com/sharer.php?u=${url}`; },
		popup: [600, 400],
	},
	Twitter: {
		url(url, text) { return `https://twitter.com/intent/tweet?url=${url}${text ? `&text=${encodeURIComponent(text)}` : ""}`; },
		popup: [600, 400],
	},
	WhatsApp: {
		url(url, text) { return `whatsapp://send?text=${url}${text ? ` ${encodeURIComponent(text)}` : ""}`; },
		attributes: { 'data-action': 'share/whatsapp/share' }
	},
	Weibo: {
		url(url, text, title) { return `https://service.weibo.com/share/share.php?url=${url}&appkey=&title=${title}`; },
		popup: [600, 400],	
	},
	EMail: {
		url(url, text, title) { return `mailto:?body=${text ? encodeURIComponent(text) : url}&subject=${encodeURIComponent(title)}`; },
	},
	LinkedIn: {
		url(url, text, title) { return `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(text)}`; },
		popup: [600, 400],
	},
	Xing: {
		url(url) { return `https://www.xing.com/spi/shares/new?url=${url}`; },
		popup: [600, 400],
	},
	Embed: {},
};

type OptionProps = {
	href: string;
	service: "Facebook" | "Twitter" | "WhatsApp" | "EMail" | "Embed" | "LinkedIn" | "Weibo" | "Xing";
	content: Publishable;
	onClick: (ev: Event) => void;
	intl: any;
};

const _SharingOption = ({href, service, content, onClick, intl: {formatMessage}}: OptionProps) => {
	const label = formatMessage(messages[`${service}Label`]);
	if (service && service !== "Embed") {
		let text = messages[`${service}Text`];
		let title = messages[`${service}Title`];
		text = text && formatMessage(text, Object.assign({share_url: href}, content));
		title = title && formatMessage(title, {title: content.title || content.name});
		href = SERVICES[service].url(encodeURIComponent(href), text, title);
		onClick = (ev: Event) => {
			trackShare(service, href);
			const popup = SERVICES[service].popup;
			if (!popup) { return; }
			window.open(href, "share", `width=${popup[0]}, height=${popup[1]}`);
			ev.preventDefault();
		};
	}

	const attrs = SERVICES[service].attributes || {};

	return (
		<li {...use(styles.Item)}>
			<a {...use(styles.Option)} onClick={onClick} href={href} title={label} {...attrs}>
				{React.createElement(Icons[service])}
			</a>
		</li>
	);
};

const SharingOption = injectIntl(_SharingOption);

type Props = {
	content: Publishable;
	intl: any;
	onClose: () => void;
};

class Share extends React.Component {
	static contextTypes = {
		player: PropTypes.object,
	};

	context: {
		player: Player;
	};

	props: Props;

	state: {
		embedOpen: boolean;
		embedSize: string;
	};

	embedCode: HTMLTextAreaElement;
	setEmbedCode = (el: HTMLTextAreaElement) => (this.embedCode = el);

	constructor(props: Props) {
		super(props);
		this.state = {
			embedOpen: false,
			embedSize: "responsive",
			href: null,
		};
	}

	toggleEmbed = () => this.setState({
		embedOpen: !this.state.embedOpen,
	})

	changeEmbedSize = (ev: Event) => this.setState({
		embedSize: (ev.target: any).value,
	})

	componentWillReceiveProps(nextProps) {
		this.getHref(nextProps);
	}

	componentDidMount() {
		this.componentDidUpdate();
		this.getHref(this.props);
	}

	componentDidUpdate() {
		if (!this.state.embedOpen) { return; }
		this.embedCode.select();
		try {
			document.execCommand("copy");
		} catch (e) {}
	}

	getHref(props) {
		const {player: {url, options, api}} = this.context;
		const {content: {id, type, publisher}} = props;

		if(this.state.id === id) { return this.state.href; }

		let href = url.create({query: {q: undefined}}, {absolute: options.shareBaseUrl + (
			options.historyBaseName ? options.historyBaseName : '/')
		});
		if (options.isEmbedded && options.topHref) {
			href = options.topHref.replace(/\?page=[0-9]+&/, '?').replace(/(&|\?)page=[0-9]+/, '');
		}

		if (options.shortenShareURL && styles.shortenShareURL) {
			post(`${api._API_BASE}api/v1/publishers/${publisher.id}/shares/`, {
				url: href,
				item: {
					id: id,
					type: type,
					context: url.hierarchy,
					locale: url.locale,
				}
			}).then(({share_url}) => {
				this.setState({ href: share_url, id });
			});
		} else {
			this.setState({ href, id });
		}
	}

	render() {
		const {player} = this.context;
		const {content, onClose, side, intl: {formatMessage}} = this.props;
		const {embedSize, embedOpen, href} = this.state;
		const scriptUrl = "//js.pagestrip.com/";
		const widget = player.url.create({query: {q: undefined, embedwidget: null}},
			{absolute: player.options.embedBaseUrl, withContext: !!~player.options.embedBaseUrl.indexOf('pagestrip.com')});

		let embedCode = `<script async src="${scriptUrl}embed.min.js"></script>\n` +
			`<!-- ${formatMessage(messages.scriptonce)} -->\n`;
		if (embedSize === "custom") {
			embedCode +=
				`<a href="${href}" class="pagestrip-trigger">\n` +
				`<!-- ${formatMessage(messages.customhere)} -->\n</a>`;
		} else if (embedSize === "responsive") {
			embedCode +=
				`<a href="${href}" data-size="${embedSize}" ` +
				`data-embed="${widget}" class="pagestrip-embed">${content.title || content.name}</a>`;
		} else {
			const context = content.id;
			const {locale} = player.options;
			embedCode =
`<script src="${scriptUrl}loader.min.js"></script>
<!-- ${formatMessage(messages.scriptonce)} -->
<div id="pagestrip"></div>
<script>
var options = {
  history: "hash", prevNext: false, jackInTheBox: true,
  context: ${JSON.stringify(context)}, locale: ${JSON.stringify(locale)}
};
Kent.renderClient(null, options, document.querySelector("#pagestrip"));
</script>`;
		}

		const options = styles.options.split(' ').map(o => o.trim()).map(o => {
			const parts = o.split('-');
			let condition = true;
			if(parts.length > 1) {
				o = parts[1];
				switch (parts[0]) {
					case 'mobile': condition = isMobile; break;
					case 'desktop': condition = !isMobile; break;
					default: condition = false; break;
				}
			} else {
				o = parts[0];
			}
			return SERVICES[o] && condition &&
				(<SharingOption href={o ===  'Embed' ? null : href} service={o} content={o ===  'Embed' ? null : content} onClick={o ===  'Embed' ? this.toggleEmbed : null} />) || null
		});

		return (
			<div {...use(styles.Container, side == 'left' ? styles.FloatLeft : side == 'right' ? styles.FloatRight : null , embedOpen && styles.EmbedOpen)}>
				<section {...use(styles.Section, embedOpen && styles.SectionEmbedOpen)}>
					<Heading lvl={3} {...use(styles.Row)}>{formatMessage(messages.share)}</Heading>
					<ul {...use(styles.Options)}>
						{options}
					</ul>
				</section>
				{(!side || embedOpen) ? 
				<React.Fragment>
					<section {...use(styles.SectionAbsolute)}>
						<Heading lvl={3} {...use(styles.Row)}>{formatMessage(messages.embed)}</Heading>
						<label {...use(styles.Row, styles.EmbedSizeContainer)}>
							<select {...use(styles.EmbedSize)} onChange={this.changeEmbedSize}>
								<option value="responsive">{formatMessage(messages.cover)}</option>
								<option value="custom">{formatMessage(messages.custom)}</option>
								<option value="inline">{formatMessage(messages.inline)}</option>
							</select>
							<Icons.EmbedSelect {...use(styles.EmbedSizeIcon)} />
						</label>
						{embedSize === "responsive" && <Cover
							useShareImage
							style={styles.EmbedCover} content={content} />}
						<textarea
							{...use(styles.EmbedCode, embedSize !== "responsive" && styles.EmbedCodeBig)}
							readOnly ref={this.setEmbedCode} value={embedCode} />
						<a {...use(styles.Row, styles.EmbedBack)} onClick={this.toggleEmbed}>{formatMessage(messages.back)}</a>
					</section>
					<button {...use(styles.Close)} onClick={onClose}><Close {...use(styles.CloseIcon)} /></button>
				</React.Fragment>
				: null }
			</div>
		);
	}
}

const ShareDropdown = ({content, side, intl}: {content: Publishable}) => side ? <Share content={content} intl={intl} side={side} /> : (
	<DropdownItem
		item="share"
		button={<Button style={styles.ShareButton} title={intl.formatMessage(messages.share)}><Icons.Share role="img" aria-label={intl.formatMessage(messages.share)} /></Button>}>
		<Share content={content} intl={intl} />
	</DropdownItem>
);

// Share Buttons
class _ShareIcon extends React.Component {
	static contextTypes = {
		player: PropTypes.object,
	};
	context: {
		player: Player;
	};
	constructor(props: Props) {
		super(props);
		this.state = {
			href: null,
		};
	}
	getHref = () => {
		const {player: {url, options, api}} = this.context;
		const {content: {id, type, publisher}} = this.props;

		if (this.state.id === id) { return this.state.href; }

		let href = url.create({query: {q: undefined}}, {absolute: options.shareBaseUrl + (
			options.historyBaseName ? options.historyBaseName : '/')
		});
		if (options.isEmbedded && options.topHref) {
			href = options.topHref.replace(/\?page=[0-9]+&/, '?').replace(/(&|\?)page=[0-9]+/, '');
		}

		if (options.shortenShareURL && styles.shortenShareURL) {
			return post(`${api._API_BASE}api/v1/publishers/${publisher.id}/shares/`, {
				url: href,
				item: {
					id: id,
					type: type,
					context: url.hierarchy,
					locale: url.locale,
				}
			}).then((data) => {
				this.setState({ href: data.share_url, id });
				return data.share_url;
			});
		} else {
			this.setState({ href, id });
		}
	}
	getItemURL = (item, shareURL) => {
		const {content, destination, intl: {formatMessage}} = this.props;
		let {text, title} = item;
		text = text && formatMessage(text, Object.assign({share_url: this.state.href}, content));
		title = title && formatMessage(title, {title: content.title || content.name});

		const urls = {
			twitter: {
				url: `https://twitter.com/intent/tweet?url=${shareURL}&text=${IS_BROWSER && document.title}`,
			},
			facebook: {
				url: `https://facebook.com/sharer.php?u=${shareURL}`,
			},
			googleplus: {
				url: `https://plus.google.com/share?url=${shareURL}`,
			},
			linkedin: {
				url: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareURL)}&title=${IS_BROWSER && document.title}&summary=${text ? ` ${encodeURIComponent(text)}` : ""}`,
			},
			weibo: {
				url: `https://service.weibo.com/share/share.php?url=${encodeURIComponent(shareURL)}&appkey=&title=${IS_BROWSER && document.title}`
			},
			email: {
				url: `mailto:?body=${text ? encodeURIComponent(text) : shareURL}&subject=${encodeURIComponent(title)}`,
			},
			whatsapp: {
				url: `whatsapp://send?text=${encodeURIComponent(shareURL)}${text ? ` ${encodeURIComponent(text)}` : ""}`,
				attributes: { 'data-action': 'share/whatsapp/share' },
			},
			xing: {
				url: `https://www.xing.com/spi/shares/new?url=${shareURL}`,
			},
		};

		return urls[destination].url;
	}
	getAttributes = () => {
		const attributes = {
			whatsapp: {
				attributes: { 'data-action': 'share/whatsapp/share' },
			},
		}

		return attributes[this.props.destination] || undefined;
	}
	getItem = () => {
		switch (this.props.destination) {
		case "facebook":
			return {
				icon: Icons.Facebook,
			};
		case "twitter":
			return {
				icon: Icons.Twitter,
			};
		case "googleplus":
			return {
				icon: Icons.GooglePlus,
			};
		case "whatsapp":
			return {
				icon: Icons.WhatsApp,
				mobile: true,
				text: messages[`WhatsAppText`],
				title: messages[`WhatsAppTitle`],
			};
		case "linkedin":
			return {
				icon: Icons.LinkedIn,
			};
		case "weibo": 
			return {
				icon: Icons.Weibo,
			};
		case "xing": 
			return {
				icon: Icons.Xing,
			};
		case "email":
			return {
				icon: Icons.EMail,
				text: messages[`EMailText`],
				title: messages[`EMailTitle`],
			};
		default:
			return null;
		}
	}
	handleClick = e => {
		e.preventDefault();
		const {destination} = this.props;

		const shareTab = destination === "email" ? window : window.open("", '_blank');
		if (this.getHref().then) {
			this.getHref().then(href => {
				shareTab.location.href = this.getItemURL(this.getItem(), href);
			});
		} else {
			shareTab.location.href = this.getItemURL(this.getItem(), this.state.href);
		}
	}
	render() {
		const {destination} = this.props;

		const Item = this.getItem;
		const attrs = this.getAttributes();

		if (Item().mobile && !isMobile) {
			return null;
		}

		const Icon = Item ? Item().icon : null;

		return (
			<div data-navbar-item={`share-${destination}`} {...use(styles.ShareIcon)}>
				<a {...use(styles.ShareIconLink)} attrs={attrs} title={`Share on ${destination}`}>
					<div onClick={this.handleClick}>
						<Icon />
						<span>{`Share on ${destination}`}</span>
					</div>
				</a>
			</div>
		);
	}
}

export const ShareIcon = injectIntl(_ShareIcon);

export default injectIntl(ShareDropdown);



// WEBPACK FOOTER //
// ./lib/navigation/share.js